import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { refreshStoredAuthUser } from "@/lib/authUser";

export interface ManagedPermissionRecord {
  id: string;
  name: string;
  title: string;
  guardName: string;
  permissionCategoryId: string | null;
  categoryName: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ManagedPermissionDraft = Pick<ManagedPermissionRecord, "name" | "title" | "permissionCategoryId">;

const LS_KEY = "inventory.managedPermissions.v1";

type PermissionApiRecord = {
  id?: number | string;
  name: string;
  title?: string | null;
  guard_name?: string;
  permission_category_id?: number | null;
  permission_category?: { id: number; name: string } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = {
  message?: string;
  data?: PermissionApiRecord;
};

type DeleteApiResponse = {
  message?: string;
};

type UpsertResult = {
  record: ManagedPermissionRecord;
  message: string;
};

type DeleteResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: ManagedPermissionRecord[]): ManagedPermissionRecord[] {
  const byId = new Map<ID, ManagedPermissionRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): ManagedPermissionRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ManagedPermissionRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: ManagedPermissionRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): PermissionApiRecord[] {
  if (Array.isArray(body)) return body as PermissionApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as PermissionApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as PermissionApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): PermissionApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as PermissionApiRecord;
  }
  return null;
}

function mapApiRecord(record: PermissionApiRecord): ManagedPermissionRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  const cat = record.permission_category;
  const catId =
    record.permission_category_id != null
      ? String(record.permission_category_id)
      : cat
        ? String(cat.id)
        : null;
  const catName = cat?.name ?? null;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    title: (record.title ?? "").trim() || record.name,
    guardName: record.guard_name ?? "web",
    permissionCategoryId: catId,
    categoryName: catName,
    createdAt: created,
    updatedAt: updated,
  };
}

export function listManagedPermissions(): ManagedPermissionRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncManagedPermissionsFromApi() {
  const collected: PermissionApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await api.get("/api/v1/managed-permissions", {
      params: { page, per_page: PER_PAGE, guard_name: "web" },
    });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 80);

  const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
  save(records);
  return listManagedPermissions();
}

export async function createManagedPermission(draft: ManagedPermissionDraft): Promise<UpsertResult> {
  const payload = {
    name: draft.name.trim(),
    title: draft.title.trim(),
    permission_category_id: Number(draft.permissionCategoryId),
    guard_name: "web",
  };
  const response = await api.post<UpsertApiResponse>("/api/v1/managed-permissions", payload);
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create permission response.");
  const record = mapApiRecord(created);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  // Permission changes can affect the current user's effective permissions.
  void refreshStoredAuthUser();

  return { record, message: response.data?.message || "Permission created successfully." };
}

export async function updateManagedPermission(id: ID, draft: ManagedPermissionDraft): Promise<UpsertResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const payload = {
    name: draft.name.trim(),
    title: draft.title.trim(),
    permission_category_id: Number(draft.permissionCategoryId),
    guard_name: "web",
  };
  const response = await api.put<UpsertApiResponse>(`/api/v1/managed-permissions/${id}`, payload);
  const updated = extractFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update permission response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);

  void refreshStoredAuthUser();

  return { record, message: response.data?.message || "Permission updated successfully." };
}

export async function deleteManagedPermission(id: ID): Promise<DeleteResult> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/managed-permissions/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  void refreshStoredAuthUser();
  return { success: removed, message: response.data?.message || "Permission deleted successfully." };
}
